import { LOG_LEVELS, logMessage } from '../logging.js';

class MobileCameraCapture {
  constructor(dropzoneManager) {
    this.dropzoneManager = dropzoneManager;
    this.root = document.getElementById('mobile-camera-capture');
    this.video = document.getElementById('mobile-camera-video');
    this.status = document.getElementById('mobile-camera-status');
    this.captureButton = document.getElementById('mobile-camera-shutter');
    this.uploadButton = document.getElementById('mobile-camera-upload');
    this.dropzoneContainer = document.getElementById('mobile-container1');
    this.modal = document.getElementById('scanModal');
    this.frame = this.root?.querySelector('.mobile-card-frame');
    this.cropFrame = this.root?.querySelector('.mobile-card-crop-frame');
    this.frameLabel = this.frame?.querySelector('span');
    this.fallbackFrame = this.dropzoneContainer?.querySelector(
      '.mobile-fallback-card-frame'
    );
    this.fallbackFrameLabel = this.fallbackFrame?.querySelector('span');
    this.stream = null;
    this.cameraRequestId = 0;
    this.orientationUpdateFrame = null;

    if (!this.root || !this.video || !this.captureButton) {
      return;
    }

    this.handleOrientationChange = this.handleOrientationChange.bind(this);
    this.captureButton.addEventListener('click', () => this.capture());
    this.uploadButton.addEventListener('click', () => this.usePhotoLibrary());
    window.addEventListener('orientationchange', this.handleOrientationChange);
    window.addEventListener('resize', this.handleOrientationChange);
    this.updateOrientation();
    this.dropzoneManager.instance().on('removedfile', () => {
      if (this.isModalOpen()) {
        this.start();
      }
    });
  }

  isModalOpen() {
    return this.modal && this.modal.style.display === 'flex';
  }

  handleOrientationChange() {
    if (this.orientationUpdateFrame) {
      cancelAnimationFrame(this.orientationUpdateFrame);
    }

    this.orientationUpdateFrame = requestAnimationFrame(() => {
      this.orientationUpdateFrame = null;
      this.updateOrientation();
    });
  }

  updateOrientation() {
    const isLandscape = window.matchMedia('(orientation: landscape)').matches;
    this.root.classList.toggle('is-landscape', isLandscape);
    this.fallbackFrame?.classList.toggle('is-landscape', isLandscape);

    if (this.frameLabel) {
      this.frameLabel.textContent = isLandscape
        ? 'Align horizontal card here'
        : 'Align card here';
    }

    if (this.fallbackFrameLabel) {
      this.fallbackFrameLabel.textContent = isLandscape
        ? 'Keep the full horizontal card inside this shape'
        : 'Keep the full card inside this shape';
    }

    if (this.stream) {
      this.setStatus(this.getOrientationInstruction(isLandscape));
    }
  }

  getOrientationInstruction(
    isLandscape = window.matchMedia('(orientation: landscape)').matches
  ) {
    return isLandscape
      ? 'Landscape detected. Center one horizontally oriented card inside the frame.'
      : 'Center one card inside the frame.';
  }

  async start() {
    if (!this.root || this.stream) {
      return;
    }

    const requestId = ++this.cameraRequestId;
    this.updateOrientation();
    this.showCamera();
    this.setStatus('Starting camera…');
    this.captureButton.disabled = true;

    if (!navigator.mediaDevices?.getUserMedia) {
      this.showPhotoLibrary(
        window.isSecureContext
          ? 'Live camera preview is unavailable in this browser. Choose a photo instead.'
          : 'Live camera preview requires HTTPS on iPhone. Use the guide below and choose a photo instead.'
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 4096 },
          height: { ideal: 4096 },
        },
      });

      if (requestId !== this.cameraRequestId || !this.isModalOpen()) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      this.stream = stream;
      this.video.srcObject = this.stream;
      await this.video.play();
      this.captureButton.disabled = false;
      this.setStatus(this.getOrientationInstruction());
    } catch (error) {
      if (requestId !== this.cameraRequestId) {
        return;
      }

      logMessage(LOG_LEVELS.INFO, 'Unable to start mobile camera', {
        error: error.message,
      });
      this.showPhotoLibrary(
        'Camera access was unavailable. Choose a photo from your device instead.'
      );
    }
  }

  stop() {
    this.cameraRequestId += 1;

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    if (this.video) {
      this.video.srcObject = null;
    }
  }

  capture() {
    if (!this.stream || !this.video.videoWidth || !this.video.videoHeight) {
      return;
    }

    this.captureButton.disabled = true;

    const cropBounds = this.getFrameCropBounds(this.cropFrame || this.frame);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(cropBounds.width);
    canvas.height = Math.round(cropBounds.height);
    const context = canvas.getContext('2d');
    context.drawImage(
      this.video,
      cropBounds.x,
      cropBounds.y,
      cropBounds.width,
      cropBounds.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    canvas.toBlob(
      (blob) => {
        if (!blob || !this.isModalOpen()) {
          this.captureButton.disabled = false;
          if (this.isModalOpen()) {
            this.setStatus(
              'The photo could not be captured. Please try again.'
            );
          }
          return;
        }

        const file = new File([blob], `card-${Date.now()}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });

        this.stop();
        this.root.hidden = true;
        this.dropzoneContainer.hidden = false;
        this.dropzoneManager.addFile(file, {
          url: URL.createObjectURL(blob),
          width: canvas.width,
          height: canvas.height,
        });
      },
      'image/jpeg',
      0.95
    );
  }

  getFrameCropBounds(frame) {
    const sourceWidth = this.video.videoWidth;
    const sourceHeight = this.video.videoHeight;

    if (!frame) {
      return {
        x: 0,
        y: 0,
        width: sourceWidth,
        height: sourceHeight,
      };
    }

    const videoRect = this.video.getBoundingClientRect();
    const frameRect = frame.getBoundingClientRect();
    const coverScale = Math.max(
      videoRect.width / sourceWidth,
      videoRect.height / sourceHeight
    );
    const renderedWidth = sourceWidth * coverScale;
    const renderedHeight = sourceHeight * coverScale;
    const renderedOffsetX = (videoRect.width - renderedWidth) / 2;
    const renderedOffsetY = (videoRect.height - renderedHeight) / 2;

    const x = (frameRect.left - videoRect.left - renderedOffsetX) / coverScale;
    const y = (frameRect.top - videoRect.top - renderedOffsetY) / coverScale;
    const width = frameRect.width / coverScale;
    const height = frameRect.height / coverScale;
    const clampedX = Math.max(0, Math.min(sourceWidth - 1, x));
    const clampedY = Math.max(0, Math.min(sourceHeight - 1, y));

    return {
      x: clampedX,
      y: clampedY,
      width: Math.max(1, Math.min(width, sourceWidth - clampedX)),
      height: Math.max(1, Math.min(height, sourceHeight - clampedY)),
    };
  }

  usePhotoLibrary() {
    this.stop();
    this.showPhotoLibrary();
    this.dropzoneManager.openFilePicker();
  }

  showCamera() {
    this.root.hidden = false;
    this.dropzoneContainer.hidden = true;
  }

  showPhotoLibrary(message = '') {
    this.stop();
    this.root.hidden = true;
    this.dropzoneContainer.hidden = false;
    this.setStatus(message);
  }

  setStatus(message) {
    if (this.status) {
      this.status.textContent = message;
    }
  }
}

export { MobileCameraCapture };
